import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  AiOnboardingDocumentType,
} from '@pharmaplan/common';
import AvatarFallback from '@pharmaplan/common/assets/icons/avatarPlaceholder.png';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as DocumentUploadIcon } from '../../../assets/svg/fileUploadIcon.svg';
import strings from '../../../localization';
import UploadDialog from '../../common/UploadDialog';
import { fileTypes } from '../../common/UploadButton/UploadButton';

import {
  createDocumentFileSchema,
  documentUploadConfigs,
  IDocumentUploadConfig,
  isFile,
  MAXIMUM_UPLOAD_FILE_SIZE_IN_KB,
} from './documentUploadConfig';
import documentUploadStyles from './documentUploadStyles';

interface IAiDocumentUploadDialog {
  documentType: AiOnboardingDocumentType | null;
  hasUploadError: boolean;
  isOpen: boolean;
  isUploading: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  requestId: number | null;
}

const useFilePreview = (file: unknown) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    if (isFile(file)) {
      objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(null);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file]);

  return previewUrl;
};

interface IAiDocumentUploadForm {
  config: IDocumentUploadConfig;
  documentType: AiOnboardingDocumentType;
  hasUploadError: boolean;
  isUploading: boolean;
  onUpload: (file: File) => Promise<void>;
}

const AiDocumentUploadForm = ({
  config,
  documentType,
  hasUploadError,
  isUploading,
  onUpload,
}: IAiDocumentUploadForm) => {
  const fileSchema = useMemo(
    () =>
      createDocumentFileSchema(config),
    [config],
  );
  const formik = useFormik({
    initialValues: {
      file: null as File | null,
    },
    validationSchema: Yup.object({
      file: fileSchema,
    }),
    onSubmit: async ({ file }) => {
      if (isFile(file) && fileSchema.isValidSync(file)) {
        await onUpload(file);
      }
    },
  });
  const selectedFile = formik.values.file;
  const previewUrl = useFilePreview(selectedFile);
  const isProfilePicture = documentType === 'profilePicture';
  const customComponent = isProfilePicture ? (
    <Avatar
      imgProps={documentUploadStyles.avatarImage}
      src={previewUrl ?? AvatarFallback}
      sx={documentUploadStyles.avatar}
    />
  ) : (
    <Box sx={documentUploadStyles.documentIcon}>
      <DocumentUploadIcon height={80} width={80} />
    </Box>
  );

  const handleClear = () => {
    formik.setFieldValue('file', null);
  };

  return (
    <>
      <UploadDialog
        acceptedFileTypes={config.acceptedFileTypes}
        allowCameraCapture={isProfilePicture}
        chooseLabel={strings.uploadFile}
        customComponent={customComponent}
        disableButton={
          formik.isSubmitting
          || isUploading
          || !fileSchema.isValidSync(selectedFile)
        }
        errorMessage={config.message}
        fileSize={MAXIMUM_UPLOAD_FILE_SIZE_IN_KB}
        fileType={fileTypes[config.fileType]}
        formik={formik}
        handleClear={handleClear}
        message={config.message}
        name="file"
      />

      <Box sx={documentUploadStyles.statusContainer}>
        {hasUploadError && !isUploading && (
          <Typography role="alert" sx={documentUploadStyles.uploadError}>
            {`${strings.error} ${strings.pleaseTryAgain}`}
          </Typography>
        )}
      </Box>
    </>
  );
};

const AiDocumentUploadDialog = ({
  documentType,
  hasUploadError,
  isOpen,
  isUploading,
  onClose,
  onUpload,
  requestId,
}: IAiDocumentUploadDialog) => {
  const config = useMemo(() => {
    if (!documentType) {
      return null;
    }

    return documentUploadConfigs[documentType];
  }, [documentType]);

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={isOpen}>
      {config && documentType && (
        <>
          <DialogTitle sx={documentUploadStyles.dialogTitle}>
            {config.title}
            <IconButton
              aria-label={strings.close}
              onClick={onClose}
              sx={documentUploadStyles.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={documentUploadStyles.dialogContent}>
            <AiDocumentUploadForm
              key={`${documentType}-${requestId}`}
              config={config}
              documentType={documentType}
              hasUploadError={hasUploadError}
              isUploading={isUploading}
              onUpload={onUpload}
            />
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default AiDocumentUploadDialog;
